import { addDays, addMonths, addWeeks, format, isSameDay } from "date-fns";
import { useMemo } from "react";
import { formatCurrency } from "../lib/utils";
import { OneTimeTransaction, RecurringTransaction } from "../types";

interface TransactionTimelineProps {
  startingBalance: number;
  startingDate: Date;
  recurringTransactions: RecurringTransaction[];
  oneTimeTransactions: OneTimeTransaction[];
  startDate: Date;
}

interface DailyBalance {
  date: Date;
  balance: number;
  transactions: Array<{
    name: string;
    amount: number;
  }>;
}

function TransactionTimeline({
  startingBalance,
  startingDate,
  recurringTransactions,
  oneTimeTransactions,
  startDate,
}: TransactionTimelineProps) {
  const timeline = useMemo(() => {
    const today = startDate;
    const endDate = addMonths(today, 3);
    const dailyBalances: DailyBalance[] = [];
    let runningBalance = startingBalance; // Start from the starting balance

    for (let date = today; date <= endDate; date = addDays(date, 1)) {
      const transactionsToday: Array<{ name: string; amount: number }> = [];

      // Check recurring transactions
      recurringTransactions.forEach((transaction) => {
        let nextDate = new Date(transaction.startDate);

        while (nextDate <= date) {
          if (isSameDay(nextDate, date)) {
            transactionsToday.push({
              name: transaction.name,
              amount: transaction.amount,
            });
          }

          // Calculate next occurrence
          switch (transaction.frequency) {
            case "weekly":
              nextDate = addWeeks(nextDate, 1);
              break;
            case "biweekly":
              nextDate = addWeeks(nextDate, 2);
              break;
            case "monthly":
              nextDate = addMonths(nextDate, 1);
              break;
          }
        }
      });

      // Check one-time transactions
      oneTimeTransactions.forEach((transaction) => {
        if (isSameDay(transaction.date, date)) {
          transactionsToday.push({
            name: transaction.name,
            amount: transaction.amount,
          });
        }
      });

      if (transactionsToday.length > 0) {
        transactionsToday.forEach((t) => {
          runningBalance += t.amount;
        });

        dailyBalances.push({
          date,
          balance: runningBalance,
          transactions: transactionsToday,
        });
      }
    }

    return dailyBalances;
  }, [
    startingBalance,
    recurringTransactions,
    oneTimeTransactions,
    startDate
  ]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2 font-medium">Date</th>
            <th className="text-left py-2 font-medium">Transactions</th>
            <th className="text-right py-2 font-medium">Balance</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-border/50">
            <td className="py-2 pr-4">{format(startDate, "MMM d, yyyy")}</td>
            <td className="py-2 pr-4 text-sm text-muted-foreground">
              Initial Balance
            </td>
            <td
              className={`py-2 text-right tabular-nums ${startingBalance >= 0 ? "text-green-600" : "text-red-600"
                }`}
            >
              {formatCurrency(startingBalance)}
            </td>
          </tr>
          {timeline.map((day, index) => (
            <tr key={index} className="border-b border-border/50 last:border-0">
              <td className="py-2 pr-4 align-top">
                {format(day.date, "MMM d, yyyy")}
              </td>
              <td className="py-2 pr-4 text-sm text-muted-foreground">
                <div className="space-y-1">
                  <div className="">
                    <span
                      className={
                        day.transactions.reduce(
                          (sum, t) => sum + t.amount,
                          0
                        ) >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }
                    >
                      {day.transactions.reduce((sum, t) => sum + t.amount, 0) >=
                        0
                        ? "+"
                        : "-"}
                      {formatCurrency(
                        day.transactions.reduce((sum, t) => sum + t.amount, 0)
                      )}
                    </span>
                  </div>
                  {day.transactions.map((t, i) => (
                    <div key={i}>
                      {t.name}:{" "}
                      <span
                        className={
                          t.amount >= 0 ? "text-green-600" : "text-red-600"
                        }
                      >
                        {t.amount >= 0 ? "+" : "-"}
                        {formatCurrency(t.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </td>
              <td
                className={`py-2 text-right tabular-nums ${day.balance >= 0 ? "text-green-600" : "text-red-600"
                  }`}
              >
                {formatCurrency(day.balance)}
              </td>
            </tr>
          ))}
          {timeline.length === 0 && startingBalance === 0 && (
            <tr>
              <td
                colSpan={3}
                className="py-4 text-center text-sm text-muted-foreground"
              >
                No transactions in the next 3 months
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default TransactionTimeline;
